import React, { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { Button } from 'primereact/button'
import { OverlayPanel } from 'primereact/overlaypanel'
import { confirmDialog } from 'primereact/confirmdialog'
import { selectMembershipApiHost } from 'globalSelectors'
import authFetch from 'api/auth/authFetch'
import {
    setAddPotentialMemberFieldAction,
    setAddPotentialMemberErrorsAction,
    setAddPotentialMemberStatusAction,
    resetAddPotentialMemberAction
} from '../../store/actions'
import { selectAddPotentialMember } from '../../store/selectors'

const SearchHeader = ({
    inputRef,
    onSearch,
    onPotentialMemberCreated,
    toastRef,
    isLoading = false
}) => {
    const dispatch = useDispatch()
    const overlayRef = useRef(null)
    const [searchTerm, setSearchTerm] = React.useState('')

    const membershipApiHost = useSelector(selectMembershipApiHost)
    const { firstName, middleName, lastName, remarks, errors, status } = useSelector(selectAddPotentialMember)

    const isSubmitting = status === 'pending'

    const handleSearch = () => {
        onSearch(searchTerm.trim())
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const handleShowOverlay = (e) => {
        dispatch(resetAddPotentialMemberAction())
        overlayRef.current?.toggle(e)
    }

    const handleHideOverlay = () => {
        overlayRef.current?.hide()
        dispatch(resetAddPotentialMemberAction())
    }

    const setField = (field, value) => dispatch(setAddPotentialMemberFieldAction(field, value))

    const validate = () => {
        const newErrors = {}
        if (!firstName.trim()) newErrors.firstName = 'First name is required'
        if (!lastName.trim()) newErrors.lastName = 'Last name is required'
        dispatch(setAddPotentialMemberErrorsAction(newErrors))
        return Object.keys(newErrors).length === 0
    }

    const checkDuplicates = async () => {
        const response = await authFetch(
            `${membershipApiHost}/Contact/CheckDuplicates?firstName=${encodeURIComponent(firstName.trim())}&lastName=${encodeURIComponent(lastName.trim())}`
        )
        return response.ok ? await response.json() : []
    }

    const createPotentialMember = async () => {
        const response = await authFetch(`${membershipApiHost}/Contact/CreatePotentialMember`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                FirstName: firstName.trim(),
                MiddleName: middleName.trim() || null,
                LastName: lastName.trim(),
                Remarks: remarks.trim() || null
            })
        })
        if (!response.ok) throw new Error('Failed to create potential member')
        return await response.json()
    }

    const handleCreate = async () => {
        dispatch(setAddPotentialMemberStatusAction('pending'))
        try {
            const result = await createPotentialMember()
            dispatch(setAddPotentialMemberStatusAction('complete'))
            toastRef?.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Potential member created successfully',
                life: 3000
            })
            overlayRef.current?.hide()
            if (onPotentialMemberCreated) {
                onPotentialMemberCreated(result)
            }
        } catch (error) {
            dispatch(setAddPotentialMemberStatusAction('error'))
            toastRef?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to create potential member',
                life: 3000
            })
        }
    }

    const handleSubmit = async () => {
        if (!validate()) return

        dispatch(setAddPotentialMemberStatusAction('pending'))
        try {
            const duplicates = await checkDuplicates()

            if (duplicates && duplicates.length > 0) {
                dispatch(setAddPotentialMemberStatusAction('idle'))
                confirmDialog({
                    message: 'Duplicate Name Entry found. Continue to create?',
                    header: 'Duplicate Detected',
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'Yes',
                    rejectLabel: 'No',
                    accept: handleCreate,
                    reject: () => {}
                })
            } else {
                await handleCreate()
            }
        } catch (error) {
            dispatch(setAddPotentialMemberStatusAction('error'))
            toastRef?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to check duplicates',
                life: 3000
            })
        }
    }

    return (
        <div className="d-flex align-items-center pb-2 flex-shrink-0">
            <IconField iconPosition="right" className="me-3">
                <InputIcon
                    className={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-search'}
                    onClick={!isLoading ? handleSearch : undefined}
                    style={{ cursor: 'pointer' }}
                />
                <InputText
                    ref={inputRef}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Search by author key, name..."
                    disabled={isLoading}
                    style={{ width: '300px' }}
                />
            </IconField>

            <Button
                label="Add a new potential member"
                icon="fa-light fa-plus"
                onClick={handleShowOverlay}
            />

            <OverlayPanel
                ref={overlayRef}
                className="add-potential-member-overlay"
                dismissable={false}
                showCloseIcon={false}
            >
                <div className="add-potential-member-form">
                    <div className="form-field">
                        <label htmlFor="firstName">First name</label>
                        <InputText
                            id="firstName"
                            value={firstName}
                            onChange={(e) => setField('firstName', e.target.value)}
                            className={errors.firstName ? 'p-invalid' : ''}
                            disabled={isSubmitting}
                        />
                        {errors.firstName && <small className="field-error">{errors.firstName}</small>}
                    </div>

                    <div className="form-field">
                        <label htmlFor="middleName">Middle name</label>
                        <InputText
                            id="middleName"
                            value={middleName}
                            onChange={(e) => setField('middleName', e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-field">
                        <label htmlFor="lastName">Last name</label>
                        <InputText
                            id="lastName"
                            value={lastName}
                            onChange={(e) => setField('lastName', e.target.value)}
                            className={errors.lastName ? 'p-invalid' : ''}
                            disabled={isSubmitting}
                        />
                        {errors.lastName && <small className="field-error">{errors.lastName}</small>}
                    </div>

                    <div className="form-field">
                        <label htmlFor="remarks">Remarks</label>
                        <InputTextarea
                            id="remarks"
                            value={remarks}
                            onChange={(e) => setField('remarks', e.target.value)}
                            rows={3}
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="add-potential-member-overlay-footer">
                        <Button
                            label="Create contributor"
                            icon="fa-light fa-check"
                            onClick={handleSubmit}
                            className="green"
                            raised
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        />
                        <Button
                            label="Cancel"
                            icon="fa-light fa-xmark"
                            onClick={handleHideOverlay}
                            className="yellow"
                            raised
                            disabled={isSubmitting}
                        />
                    </div>
                </div>
            </OverlayPanel>
        </div>
    )
}

export default SearchHeader
